import { useEffect, useMemo, useRef, useState } from 'react';
import { Building2, Check, ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useClientOptions } from '@/features/clients/api';
import { useUiStore } from '@/stores/ui.store';

export interface ClientSwitcherProps {
  /** Quando falso, esconde a opção "Todos os clientes". */
  allowAll?: boolean;
  className?: string;
}

/** Seletor de cliente do topo: define o escopo de trabalho da sessão. */
export function ClientSwitcher({ allowAll = true, className }: ClientSwitcherProps) {
  const { data: clients, isLoading, isError } = useClientOptions();
  const selectedClientId = useUiStore((state) => state.selectedClientId);
  const setSelectedClientId = useUiStore((state) => state.setSelectedClientId);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  // Papéis com escopo de um único cliente já entram travados nele.
  useEffect(() => {
    if (!allowAll && !selectedClientId && clients && clients.length > 0) {
      setSelectedClientId(clients[0].id);
    }
  }, [allowAll, clients, selectedClientId, setSelectedClientId]);

  const selected = useMemo(
    () => clients?.find((client) => client.id === selectedClientId) ?? null,
    [clients, selectedClientId],
  );

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!clients) return [];
    if (!term) return clients;
    return clients.filter((client) => client.name.toLowerCase().includes(term));
  }, [clients, query]);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Selecionar cliente"
        onClick={() => setOpen((current) => !current)}
        className="flex h-9 max-w-[16rem] items-center gap-2 rounded-lg border border-line bg-surface px-2.5 text-sm text-fg transition hover:bg-elevated"
      >
        <Building2 className="h-4 w-4 shrink-0 text-muted" aria-hidden />
        <span className="truncate">
          {isLoading
            ? 'Carregando…'
            : isError
              ? 'Erro ao carregar'
              : (selected?.name ?? (allowAll ? 'Todos os clientes' : 'Selecione um cliente'))}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted" aria-hidden />
      </button>

      {open && (
        <div className="absolute left-0 z-40 mt-1 w-72 overflow-hidden rounded-lg border border-line bg-surface shadow-pop">
          <div className="flex items-center gap-2 border-b border-line px-3 py-2">
            <Search className="h-3.5 w-3.5 text-muted" aria-hidden />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar cliente…"
              aria-label="Buscar cliente"
              className="w-full bg-transparent text-sm text-fg outline-none placeholder:text-muted"
            />
          </div>
          <ul role="listbox" className="max-h-72 overflow-y-auto py-1">
            {allowAll && (
              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={selectedClientId === null}
                  onClick={() => {
                    setSelectedClientId(null);
                    setOpen(false);
                  }}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition hover:bg-elevated"
                >
                  <span className="text-fg">Todos os clientes</span>
                  {selectedClientId === null && <Check className="h-4 w-4 text-primary" aria-hidden />}
                </button>
              </li>
            )}
            {filtered.length === 0 && (
              <li className="px-3 py-3 text-sm text-muted">
                {isLoading ? 'Carregando clientes…' : 'Nenhum cliente encontrado'}
              </li>
            )}
            {filtered.map((client) => (
              <li key={client.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={client.id === selectedClientId}
                  onClick={() => {
                    setSelectedClientId(client.id);
                    setOpen(false);
                  }}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition hover:bg-elevated"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-fg">{client.name}</span>
                    {!client.isActive && <span className="text-xs text-warning">Inativo</span>}
                  </span>
                  {client.id === selectedClientId && <Check className="h-4 w-4 text-primary" aria-hidden />}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
